import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, ArrowLeft, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { API } from '../config';

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_no-sub-pdf/artifacts/8avttwow_logohexoworkssanstitre.png";

const labels = {
  en: {
    title: 'Forgot your password?',
    subtitle: 'Enter your email and we\'ll send you a reset link.',
    email: 'Email',
    submit: 'Send reset link',
    sent_title: 'Check your inbox',
    sent_text: 'If the email exists, a reset link has been sent. Check your spam folder too.',
    back: 'Back to login',
  },
  fr: {
    title: 'Mot de passe oublié ?',
    subtitle: 'Entrez votre email et nous vous enverrons un lien de réinitialisation.',
    email: 'Email',
    submit: 'Envoyer le lien',
    sent_title: 'Vérifiez votre boîte mail',
    sent_text: 'Si l\'email existe, un lien de réinitialisation a été envoyé. Pensez à vérifier vos spams.',
    back: 'Retour à la connexion',
  },
  hi: {
    title: 'पासवर्ड भूल गए?',
    subtitle: 'अपना ईमेल दर्ज करें और हम आपको रीसेट लिंक भेजेंगे।',
    email: 'ईमेल',
    submit: 'रीसेट लिंक भेजें',
    sent_title: 'अपना इनबॉक्स जांचें',
    sent_text: 'यदि ईमेल मौजूद है, तो एक रीसेट लिंक भेजा गया है। अपना स्पैम फ़ोल्डर भी जांचें।',
    back: 'लॉगिन पर वापस जाएं',
  },
};

export default function ForgotPasswordPage() {
  const { lang } = useI18n();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const l = labels[lang] || labels.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      toast.error(lang === 'fr' ? 'Une erreur est survenue.' : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="forgot-password-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/10 via-transparent to-transparent" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-sm">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-white text-sm mb-8 transition-colors" data-testid="forgot-back-link">
          <ArrowLeft className="w-4 h-4" />
          {l.back}
        </Link>

        <div className="flex items-center gap-2.5 mb-8">
          <img src={LOGO_ICON} alt="HexoPDF" className="w-8 h-8" />
          <span className="font-heading font-bold text-xl text-foreground">Hexo<span className="text-blue-500">PDF</span></span>
        </div>

        {sent ? (
          <div data-testid="forgot-success">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-5">
              <Mail className="w-6 h-6 text-blue-400" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-foreground mb-2">{l.sent_title}</h1>
            <p className="text-muted-foreground text-sm leading-relaxed">{l.sent_text}</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-semibold text-foreground mb-1" data-testid="forgot-title">{l.title}</h1>
            <p className="text-muted-foreground text-sm mb-8">{l.subtitle}</p>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-form">
              <div>
                <Label className="text-muted-foreground text-sm mb-1.5 block">{l.email}</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-card border-border focus:border-blue-500 text-foreground h-11 rounded-lg"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-full h-11 font-medium shadow-[0_0_15px_rgba(59,130,246,0.4)] hover:shadow-[0_0_25px_rgba(59,130,246,0.6)] transition-all"
                data-testid="forgot-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : l.submit}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
